import type * as React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import DatePicker from '@cloudscape-design/components/date-picker';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { toast } from '@/lib/notifications';
import { postponeRenewalAdminCallable } from '@/lib/callables';
import type { Subscription, TenantId } from '@etiquetachef/types';
import { isPostponeDateEnabled, postponeDayStartMs } from './postponeDueDate';
import { serverNow } from '@etiquetachef/ui-shared/online';

type Props = {
  open: boolean;
  tenantId: TenantId;
  tenantName: string;
  /** Fuso IANA do tenant (`tenant.timeZone`): a meia-noite do dia escolhido é a dele. */
  timeZone: string;
  subscription: Subscription | null;
  onClose: () => void;
};

/**
 * Prorroga o próximo vencimento da assinatura (super-admin): calendário para a
 * nova data e motivo opcional.
 *
 * Origem: sem issue — commit f1f9556aa, 2026-06-11 19:41 +0200.
 *
 * Contrato: `postponeRenewalAdminCallable({ tenantId, newDueAt, reason? })`,
 * `newDueAt` = meia-noite do dia escolhido NO FUSO DO TENANT (`timeZone`),
 * em epoch ms. As datas exibidas (vencimento atual, toast) também saem nesse
 * fuso. Só adia: o
 * calendário desabilita datas até o vencimento atual (inclusive), e o servidor
 * recusa `invalid-argument` do mesmo jeito. O servidor empurra o
 * `current_period_end` no gateway SEM cobrar o intervalo (Stripe `trial_end`;
 * Asaas desde a #751) ANTES de gravar — se o gateway falhar, nada é escrito.
 * Toast de sucesso mostra `newPeriodEnd` confirmado pelo gateway.
 *
 * O servidor exige `ADMIN_PERMISSIONS.manageSubscriptions` (ROOT/FINANCE; staff only).
 *
 * Usado por: `BillingActions` (só venda direta com assinatura viva) →
 * `routes/TenantDetailPage.tsx` (rota `/tenants/:id`, menu Tenants), admin
 * web. Spec: `docs/screens/tenant-detail-admin.md`.
 *
 * Dados: lê `subscription.current_period_end` (epoch ms) para o rótulo e o
 * piso do calendário; o servidor grava `renewal_postponed_from/at`, que o
 * `BillingCard` mostra (`apps/functions/src/billing/postponeRenewalAdmin.ts`).
 *
 * Histórico:
 *  - 2026-06-11 19:47 +0200 — `DatePicker` em `long-localized`.
 *  - 2026-10-10 14:40 +0200 — (#798) rota, menu e permissão (#796).
 *  - 2026-10-13 10:40 +0200 — (#935, item 6) meia-noite no fuso do tenant.
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function PostponeDueDateDialog({
  open,
  tenantId,
  tenantName,
  timeZone,
  subscription,
  onClose,
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentDue = subscription?.current_period_end ?? null;

  const currentDueLabel = useMemo(() => {
    if (currentDue == null) return '—';
    return new Date(currentDue).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    });
  }, [currentDue, i18n.language, timeZone]);

  function close(): void {
    setValue('');
    setReason('');
    setError(null);
    onClose();
  }

  /**
   * Envia a nova data ao `postponeRenewalAdmin` e mostra a data que o gateway confirmou.
   *
   * Origem: sem issue — commit f1f9556aa, 2026-06-11 19:41 +0200.
   *
   * Contrato: sem data → erro local. Input
   * `{ "tenantId": "tenant-1", "newDueAt": 1767225600000, "reason"?: "..." }`
   * (`newDueAt` epoch ms; `reason` só se preenchido). Saída `newPeriodEnd`
   * (epoch ms) vai para o toast. Erro → `err.message` (sem tradução), num `Alert`
   * dentro do modal (#1021 — o toast de erro ficava atrás do overlay).
   *
   * Usado por: botão "Confirmar" deste diálogo.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   */
  async function handleSubmit(): Promise<void> {
    const newDueAt = postponeDayStartMs(value, timeZone);
    if (newDueAt == null) {
      setError(t('billing.postpone.errorNoDate'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await postponeRenewalAdminCallable({
        tenantId,
        newDueAt,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      toast.success(
        t('billing.postpone.success', {
          date: new Date(result.newPeriodEnd).toLocaleDateString(i18n.language, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone,
          }),
        }),
      );
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('billing.postpone.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={!value}
              loading={submitting}
              onClick={(): void => {
                void handleSubmit();
              }}
            >
              {t('billing.postpone.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('billing.postpone.description', { name: tenantName, date: currentDueLabel })}
        </Box>
        <FormField label={t('billing.postpone.dateLabel')}>
          <DatePicker
            value={value}
            onChange={({ detail }): void => {
              setValue(detail.value);
            }}
            // Mesmo piso do servidor (`newDueAt > max(agora, current_period_end)`),
            // medido na meia-noite do tenant que vai ser enviada (#935, item 6).
            isDateEnabled={(date): boolean =>
              isPostponeDateEnabled(date, timeZone, currentDue, serverNow())
            }
            format="long-localized"
            openCalendarAriaLabel={(selected): string =>
              t('billing.postpone.openCalendar') + (selected ? `, ${selected}` : '')
            }
            nextMonthAriaLabel={t('billing.postpone.nextMonth')}
            previousMonthAriaLabel={t('billing.postpone.prevMonth')}
            todayAriaLabel={t('billing.postpone.today')}
          />
        </FormField>
        <FormField label={t('billing.postpone.reasonLabel')}>
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
            }}
            placeholder={t('billing.postpone.reasonPlaceholder')}
            rows={2}
          />
        </FormField>
        <Alert type="info">{t('billing.postpone.info')}</Alert>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
